import { Injectable, inject, Renderer2, RendererFactory2 } from '@angular/core';
import { DOCUMENT } from '@angular/common';

export type AnnouncementPriority = 'polite' | 'assertive';

@Injectable({
  providedIn: 'root'
})
export class AnnouncementService {
  private readonly document = inject(DOCUMENT);
  private readonly renderer: Renderer2;
  private liveRegion: HTMLElement | null = null;
  private announcementQueue: string[] = [];
  private isProcessing = false;

  constructor() {
    // Initialize Renderer2 from RendererFactory2
    const rendererFactory = inject(RendererFactory2);
    this.renderer = rendererFactory.createRenderer(null, null);
    this.createLiveRegion();
  }

  /**
   * Announce a message to screen readers
   */
  announce(message: string, priority: AnnouncementPriority = 'polite', delay = 100): void {
    if (!message.trim()) {return;}

    this.announcementQueue.push(message);

    if (!this.isProcessing) {
      this.processQueue(priority, delay);
    }
  }

  /**
   * Announce a page change
   */
  announcePageChange(pageName: string): void {
    this.announce(`Navigated to ${pageName}`, 'assertive');
  }

  /**
   * Announce a form error
   */
  announceError(error: string): void {
    this.announce(`Error: ${error}`, 'assertive');
  }

  /**
   * Announce a success message
   */
  announceSuccess(message: string): void {
    this.announce(`Success: ${message}`, 'polite');
  }

  /**
   * Announce loading state
   */
  announceLoading(isLoading: boolean, context = ''): void {
    const message = isLoading
      ? `Loading ${context}`.trim()
      : `Finished loading ${context}`.trim();
    this.announce(message, 'polite');
  }

  /**
   * Announce count updates
   */
  announceCount(itemType: string, count: number): void {
    const message = count === 0
      ? `No ${itemType} found`
      : count === 1
      ? `1 ${itemType} found`
      : `${count} ${itemType} found`;
    this.announce(message, 'polite');
  }

  /**
   * Clear all pending announcements
   */
  clearQueue(): void {
    this.announcementQueue = [];
  }

  private createLiveRegion(): void {
    if (typeof this.document === 'undefined') {return;}

    // Use Renderer2 for platform-agnostic DOM manipulation
    this.liveRegion = this.renderer.createElement('div');

    // Set ARIA attributes
    this.renderer.setAttribute(this.liveRegion, 'role', 'status');
    this.renderer.setAttribute(this.liveRegion, 'aria-live', 'polite');
    this.renderer.setAttribute(this.liveRegion, 'aria-atomic', 'true');
    this.renderer.addClass(this.liveRegion, 'sr-only');

    // Set styles for screen-reader-only visibility
    this.renderer.setStyle(this.liveRegion, 'position', 'absolute');
    this.renderer.setStyle(this.liveRegion, 'left', '-10000px');
    this.renderer.setStyle(this.liveRegion, 'width', '1px');
    this.renderer.setStyle(this.liveRegion, 'height', '1px');
    this.renderer.setStyle(this.liveRegion, 'overflow', 'hidden');

    // Append to body
    this.renderer.appendChild(this.document.body, this.liveRegion);
  }

  private async processQueue(priority: AnnouncementPriority, delay: number): Promise<void> {
    if (this.announcementQueue.length === 0) {
      this.isProcessing = false;
      return;
    }

    this.isProcessing = true;
    const message = this.announcementQueue.shift();

    if (message && this.liveRegion) {
      // Set priority
      this.renderer.setAttribute(this.liveRegion, 'aria-live', priority);

      // Clear and set message
      this.renderer.setProperty(this.liveRegion, 'textContent', '');
      await this.wait(delay);
      this.renderer.setProperty(this.liveRegion, 'textContent', message);

      // Clear after announcement
      await this.wait(1000);
      this.renderer.setProperty(this.liveRegion, 'textContent', '');
    }

    // Process next message
    if (this.announcementQueue.length > 0) {
      await this.wait(100);
      this.processQueue(priority, delay);
    } else {
      this.isProcessing = false;
    }
  }

  private wait(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}